import {ChangeDetectionStrategy, Component, computed, inject, OnInit, signal} from '@angular/core';
import {RouterLink} from '@angular/router';
import {NgbPagination} from '@ng-bootstrap/ng-bootstrap';
import {TradeObservationDto, TradeObservationType} from '../../../../models/generated-models';
import {FruitIconComponent} from '../../../shared/commons/fruit-icon/fruit-icon.component';
import {PageTitleComponent} from '../../../shared/commons/page-title/page-title.component';
import {ThaiDateTimePipe} from '../../../shared/commons/thai-datetime.pipe';
import {RecordCodeComponent} from '../../../shared/commons/trade/record-code.component';
import {TradeApiService} from '../trade-api.service';
import {TRADE_BASE, tradeKindBadgeClass, tradeKindLabel, tradeRecordLabel} from '../trade.constants';
import {formatQuantityWithUnit} from '../trade-utils';

/** Labels of the report's observation types, in the order the filter shows them. */
export const OBSERVATION_TYPE_LABELS: Record<TradeObservationType, string> = {
  NO_ACFS_CERTIFICATE: 'ไม่มีใบรับรอง มกษ. (เสี่ยงต่อการสวมสิทธิ์)',
  STARTED_MID_CHAIN: 'บันทึกต้นทางกลางห่วงโซ่ (ไม่ใช่แหล่งปลูก)',
  MIXED_WITH_MID_CHAIN_LOT: 'พบการรวมผลผลิตจากล็อตกลางห่วงโซ่'
};

export const OBSERVATION_TYPE_SHORT_LABELS: Record<TradeObservationType, string> = {
  NO_ACFS_CERTIFICATE: 'ไม่มีใบรับรอง มกษ.',
  STARTED_MID_CHAIN: 'ต้นทางกลางห่วงโซ่',
  MIXED_WITH_MID_CHAIN_LOT: 'ปนล็อตกลางห่วงโซ่'
};

export const OBSERVATION_TYPE_BADGES: Record<TradeObservationType, string> = {
  NO_ACFS_CERTIFICATE: 'text-bg-danger',
  STARTED_MID_CHAIN: 'text-bg-warning',
  MIXED_WITH_MID_CHAIN_LOT: 'text-bg-warning'
};

/**
 * The officer report of observations (ข้อสังเกต) over every trade record: origin lots without a
 * ThaiTraces certificate, lots that started mid-chain, and sales that pooled such a lot with farm
 * goods. Staff only — the route is gated and the backend answers 403 to anyone else.
 */
@Component({
  selector: 'app-trade-observations',
  standalone: true,
  imports: [
    RouterLink,
    NgbPagination,
    PageTitleComponent,
    ThaiDateTimePipe,
    FruitIconComponent,
    RecordCodeComponent
  ],
  templateUrl: './trade-observations.component.html',
  styleUrl: './trade-observations.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TradeObservationsComponent implements OnInit {
  private readonly api = inject(TradeApiService);

  protected readonly loading = signal(false);
  protected readonly items = signal<TradeObservationDto[]>([]);
  protected readonly typeFilter = signal<TradeObservationType | null>(null);
  protected readonly search = signal('');
  protected readonly selectedProduct = signal('');
  protected readonly page = signal(1);
  protected readonly pageSize = signal(20);
  protected readonly pageSizeOptions = [10, 20, 50];

  protected readonly types: TradeObservationType[] = ['NO_ACFS_CERTIFICATE', 'STARTED_MID_CHAIN', 'MIXED_WITH_MID_CHAIN_LOT'];

  protected readonly countByType = computed<Record<TradeObservationType, number>>(() => {
    const counts: Record<TradeObservationType, number> = {NO_ACFS_CERTIFICATE: 0, STARTED_MID_CHAIN: 0, MIXED_WITH_MID_CHAIN_LOT: 0};
    for (const item of this.items()) {
      if (item.type) {
        counts[item.type] += 1;
      }
    }
    return counts;
  });

  protected readonly productOptions = computed(() => {
    const names = new Set<string>();
    for (const item of this.items()) {
      const name = (item.productName ?? '').trim();
      if (name) {
        names.add(name);
      }
    }
    return [...names].sort((a, b) => a.localeCompare(b, 'th'));
  });

  protected readonly filtered = computed<TradeObservationDto[]>(() => {
    const type = this.typeFilter();
    const product = this.selectedProduct();
    const term = this.search().trim().toLowerCase();
    return this.items().filter((item) => {
      if (type && item.type !== type) {
        return false;
      }
      if (product && (item.productName ?? '') !== product) {
        return false;
      }
      if (!term) {
        return true;
      }
      return [item.businessTitle, item.ownerName, item.productName, item.recordCode, item.message]
        .some((value) => `${value ?? ''}`.toLowerCase().includes(term));
    });
  });

  protected readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.filtered().length / this.pageSize()))
  );

  protected readonly pagedItems = computed(() => {
    const p = Math.min(this.page(), this.totalPages());
    const start = (p - 1) * this.pageSize();
    return this.filtered().slice(start, start + this.pageSize());
  });

  protected readonly tradeBase = TRADE_BASE;
  protected readonly tradeKindLabel = tradeKindLabel;
  protected readonly tradeRecordLabel = tradeRecordLabel;
  protected readonly tradeKindBadgeClass = tradeKindBadgeClass;
  protected readonly formatQuantityWithUnit = formatQuantityWithUnit;

  ngOnInit(): void {
    this.reload();
  }

  protected reload(): void {
    this.loading.set(true);
    this.api.listObservations().subscribe({
      next: (items) => {
        this.items.set(items ?? []);
        this.loading.set(false);
      },
      error: () => {
        this.items.set([]);
        this.loading.set(false);
      }
    });
  }

  protected setType(type: TradeObservationType | null): void {
    this.typeFilter.set(this.typeFilter() === type ? null : type);
    this.page.set(1);
  }

  protected onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value ?? '');
    this.page.set(1);
  }

  protected onProductChange(event: Event): void {
    this.selectedProduct.set((event.target as HTMLSelectElement).value);
    this.page.set(1);
  }

  protected onPageSizeChange(event: Event): void {
    const newSize = Number((event.target as HTMLSelectElement).value);
    this.pageSize.set(newSize);
    this.page.set(1);
  }

  protected setPage(page: number): void {
    this.page.set(page);
  }

  protected itemRowNumber(index: number): number {
    return (this.page() - 1) * this.pageSize() + index + 1;
  }

  protected typeLabel(type: TradeObservationType | undefined): string {
    return type ? OBSERVATION_TYPE_LABELS[type] ?? type : '-';
  }

  protected typeShortLabel(type: TradeObservationType | undefined): string {
    return type ? OBSERVATION_TYPE_SHORT_LABELS[type] ?? this.typeLabel(type) : '-';
  }

  protected typeBadge(type: TradeObservationType | undefined): string {
    return type ? OBSERVATION_TYPE_BADGES[type] ?? 'text-bg-secondary' : 'text-bg-secondary';
  }
}
